import { HTTP_BAD_REQUEST, HTTP_OK, HTTP_NOT_FOUND, METHOD_POST } from './arch/constants'

import {
  get,
  query,
  patch,
  post,
  postUpsert,
  put,
  del,
  directHead,
  authorizedFetch,
  postAccepted,
  postSearch,
  statusCheck,
  okCheck,
  noContentCheck
} from './auth'

import { emitNativeError } from './common'
import model from './model'

const getFeed = householdId => get(`home${householdId ? `/${householdId}` : ''}`)

const getReviewPrompt = () => get('review-prompt')
const postReviewPromptEvent = type => post('review-prompt/events', { type })

const getHousehold = id => get(`households/${id}`).then(householdContainer => householdContainer.household)
// The server returns the updated household in a container for admins and navigators but bare for
// parents, so both are accepted until the back end settles on one.
const patchHousehold = (id, payload) =>
  patch(`households/${id}`, payload).then(response => response.household ?? response)

// Decides one Andy permission from within a chat; the server also removes the request from the
// message and echoes the decision to the thread. The household comes back bare, like a plain PATCH.
const patchHouseholdAndyPermission = (id, payload) => patch(`households/${id}/andy-permission`, payload)

const getGoalHouseholds = params => query('me/goalhouseholds', params)
const getNavigatedHouseholds = params => query('me/navigatedhouseholds', params)

const getClients = () => get('clients').then(clientsContainer => clientsContainer.clients)
const getClient = id => get(`clients/${id}`).then(clientContainer => clientContainer.client)
const getClientTeam = id => get(`clients/${id}/team`).then(teamContainer => teamContainer.users)

const getProfileDocuments = (childId, householdId) =>
  get(`clients/${childId}/profile-templates${householdId ? `?householdId=${householdId}` : ''}`)
const postProfileDocuments = (childId, payload) => post(`clients/${childId}/profile-templates`, payload)
const postCreateAnotherProfileDocument = (documentId, payload) =>
  postSearch(`profile-templates/${documentId}/versions`, payload)

const patchWizardBlocks = (documentId, payload) => patch(`profile-templates/${documentId}`, payload)
const patchProfileSummaryOverride = (documentId, payload) =>
  patch(`profile-templates/${documentId}/summary-override`, payload)

const patchRecordVisibility = (documentId, payload) =>
  patch(`profile-templates/${documentId}/record-visibility`, payload)

const patchWizardSchoolBlock = (documentId, payload) => patch(`profile-templates/${documentId}/school-support`, payload)
const postWizardCheckpoint = (documentId, payload) => postSearch(`profile-templates/${documentId}/checkpoints`, payload)

const generateProfileDocuments = (documentId, payload) =>
  post(`profile-templates/${documentId}/reports`, payload).then(profileReport => profileReport.post)

const getDocumentGenerationStatus = documentId => get(`profile-documents/${documentId}/status`)

const postHideAnnouncements = (announcementId, householdId) =>
  postSearch(`announcements/${announcementId}/hide${householdId ? `/${householdId}` : ''}`)

const postDocumentExtraction = (clientId, documentId, documentType = 'areas_of_need') =>
  postAccepted(`clients/${clientId}/documents/extract`, { documentId, documentType })

// Read options shared by both plan getters. The server keys AAM-specific extraction-completion
// behavior off these—it emits the AAM notification and skips the completion email only when
// `profileTemplateId` arrives—so a read that means to send them must reach the endpoint with them
// attached. Both getters build their query string here so the two cannot drift apart.
const planReadParams = ({ suppressCompletionEmail, profileTemplateId } = {}) => {
  const params = new URLSearchParams()

  if (suppressCompletionEmail) {
    params.set('suppressCompletionEmail', 'true')
  }

  if (profileTemplateId) {
    params.set('profileTemplateId', profileTemplateId)
  }

  // The encoded string rather than the object: it doubles as the “any parameters?” test without
  // relying on URLSearchParams.size, which Safari did not implement until 17.
  return params.toString()
}

const getIep = (id, options) => {
  const search = planReadParams(options)
  const request = search ? query(`ieps/${id}`, search) : get(`ieps/${id}`)
  return request.then(iepContainer => iepContainer.iep)
}

const patchIep = (id, payload) => patch(`ieps/${id}`, payload).then(iepContainer => iepContainer.iep)
const patchIepVisibility = (id, { visible }) => patch(`ieps/${id}/visibility`, { visible })

// IEP/504 and IPP report endpoints are identical apart from the resource segment ('ieps' vs
// 'ipps') and the action segment—see domain/plan's PLAN_PRINT_FUNCTIONS, which binds `resource`
// per plan type so callers (useIepPrint's executePrint/executeSaveToBinder, PrintDialog's preview
// fetch) never pass it directly. `payload` carries notesOnly/selectedSections/onlyItemsWithNotes,
// forwarded verbatim with no defaults applied here, since every caller already supplies the full
// shape.
const postPlanReportAction = (resource, id, action, payload) => postSearch(`${resource}/${id}/${action}`, payload)

// Generates the PDF used for printing/viewing (the "Print" action).
const generatePlanReportForPrint = (resource, id, payload) => postPlanReportAction(resource, id, 'print', payload)

// Saves the PDF to the client's binder as a post (the "Save to Binder" action)—unlike
// generatePlanReportForPrint, there's no printable URL, so callers get the created post instead
// (e.g., to link to it in the binder), matching the `{ post }` container convention other
// binder-post-creating endpoints (e.g. generateProfileDocuments) already use.
const savePlanReportToBinder = (resource, id, payload) =>
  postPlanReportAction(resource, id, 'reports', payload).then(({ post }) => post)

// Unlike the other plan endpoints, this one returns a raw HTML string rather than a JSON container, so
// it can’t go through postSearch (which always parses the body as JSON).
const previewPlanReport = (resource, id, payload) =>
  authorizedFetch(`${resource}/${id}/preview`, {
    method: METHOD_POST,
    body: JSON.stringify(payload)
  })
    .then(okCheck, emitNativeError)
    .then(response => response.text())

const postIepConcern = (id, payload) => post(`ieps/${id}/concerns`, payload).then(({ concern }) => concern)

const getSchoolPlanDocumentExtractionData = id => get(`ieps/${id}/extraction`)
const patchIepExtractionData = (id, data) => patch(`ieps/${id}/extraction`, data)

const patchIepConcern = (id, payload) =>
  patch(`ieps/${id}/concerns/${payload.id}`, payload).then(({ concern }) => concern)

const patchIepRecommendationStatus = (iepId, recommendationId, statusPayload) =>
  patch(`ieps/${iepId}/recommendations/${recommendationId}`, statusPayload).then(({ recommendation }) => recommendation)

const patchIepGoalReviewStatus = (iepId, goalId, reviewStatus) =>
  patch(`ieps/${iepId}/goals/${goalId}`, { reviewStatus }).then(({ goal }) => goal)

const patchIepGoalReviewStatuses = (iepId, goalIdsWithStatuses) => patch(`ieps/${iepId}/goals`, goalIdsWithStatuses)

const patchIepServiceReviewStatus = (iepId, serviceId, reviewStatus) =>
  patch(`ieps/${iepId}/services/${serviceId}`, { reviewStatus }).then(({ service }) => service)

const patchIepServiceReviewStatuses = (iepId, serviceIdsWithStatuses) =>
  patch(`ieps/${iepId}/services`, serviceIdsWithStatuses)

const patchIepAccommodationReviewStatus = (iepId, accommodationId, reviewStatus) =>
  patch(`ieps/${iepId}/accommodations/${accommodationId}`, { reviewStatus }).then(({ accommodation }) => accommodation)

const patchIepAccommodationReviewStatuses = (iepId, accommodationIdsWithStatuses) =>
  patch(`ieps/${iepId}/accommodations`, accommodationIdsWithStatuses)

const patchIepPlopReviewStatus = (iepId, plopId, reviewStatus) =>
  patch(`ieps/${iepId}/plops/${plopId}`, { reviewStatus }).then(({ plop }) => plop)

const getIpp = (id, options) => {
  const search = planReadParams(options)
  const request = search ? query(`ipps/${id}`, search) : get(`ipps/${id}`)
  return request.then(ippContainer => ippContainer.ipp)
}

const getIppExtractionData = id => get(`ipps/${id}/extraction`)
const patchIppExtractionData = (id, data) => patch(`ipps/${id}/extraction`, data)

const postIppConcern = (id, payload) => post(`ipps/${id}/concerns`, payload).then(({ concern }) => concern)

const patchIppConcern = (id, payload) =>
  patch(`ipps/${id}/concerns/${payload.id}`, payload).then(({ concern }) => concern)

const patchIppRecommendationStatus = (ippId, recommendationId, statusPayload) =>
  patch(`ipps/${ippId}/recommendations/${recommendationId}`, statusPayload).then(({ recommendation }) => recommendation)

const patchIppPriorityReviewStatus = (ippId, priorityId, reviewStatus) =>
  patch(`ipps/${ippId}/priorities/${priorityId}`, { reviewStatus }).then(({ priority }) => priority)

const patchIppPriorityReviewStatuses = (ippId, priorityIdsWithStatuses) =>
  patch(`ipps/${ippId}/priorities`, priorityIdsWithStatuses)

const patchIppOutcomeReviewStatus = (ippId, outcomeId, reviewStatus) =>
  patch(`ipps/${ippId}/outcomes/${outcomeId}`, { reviewStatus }).then(({ outcome }) => outcome)

const patchIppOutcomeReviewStatuses = (ippId, outcomeIdsWithStatuses) =>
  patch(`ipps/${ippId}/outcomes`, outcomeIdsWithStatuses)

const patchIppGoalReviewStatus = (ippId, goalId, reviewStatus) =>
  patch(`ipps/${ippId}/goals/${goalId}`, { reviewStatus }).then(({ goal }) => goal)

const patchIppGoalReviewStatuses = (ippId, goalIdsWithStatuses) => patch(`ipps/${ippId}/goals`, goalIdsWithStatuses)

const patchIppServiceReviewStatus = (ippId, serviceId, reviewStatus) =>
  patch(`ipps/${ippId}/services/${serviceId}`, { reviewStatus }).then(({ service }) => service)

const patchIppServiceReviewStatuses = (ippId, serviceIdsWithStatuses) =>
  patch(`ipps/${ippId}/services`, serviceIdsWithStatuses)

const patchIppSupportReviewStatus = (ippId, supportId, reviewStatus) =>
  patch(`ipps/${ippId}/supports/${supportId}`, { reviewStatus }).then(({ support }) => support)

const patchIppSupportReviewStatuses = (ippId, supportIdsWithStatuses) =>
  patch(`ipps/${ippId}/supports`, supportIdsWithStatuses)

const getPostById = id => get(`posts/${id}`).then(postContainer => postContainer.post)

const getEligibleDocuments = householdId =>
  get(`households/${householdId}/eligibility/doc-extractions`).then(({ children }) => children)

const getHouseholdRatings = householdId => get(`households/${householdId}/ratings`)
const postHouseholdRating = (householdId, rating) => post(`households/${householdId}/ratings`, rating)

const downloadPostArchive = postId => {
  const params = new URLSearchParams()
  params.set('disposition', 'attachment')
  return query(`posts/${postId}/archive`, params)
}

// Unique behavior alert: A missing post document is actually quite normal, so we "accept" HTTP_BAD_REQUEST statuses.
const requestPostDocument = (postId, params) => {
  const route = `posts/${postId}/document`
  return authorizedFetch(params ? `${route}?${params}` : route)
    .then(statusCheck([HTTP_OK, HTTP_BAD_REQUEST]), emitNativeError)
    .then(response => response.json())
}

const downloadPostDocument = postId => {
  const params = new URLSearchParams()
  params.set('disposition', 'attachment')
  return requestPostDocument(postId, params)
}

const getPostDocument = requestPostDocument

const getPostGoals = postId => get(`posts/${postId}/goals`).then(goalsContainer => goalsContainer.goals)
const putPostGoals = (postId, goalIds) => put(`posts/${postId}/goals`, { goalIds })

const getPostSources = (householdId, childId, queryTerm) => {
  const params = new URLSearchParams()
  params.set('query', queryTerm)

  // If we have been given a household ID, we stop there so that the search isn’t too strictly scoped
  // (i.e., terms only showing up if used with the given child). We use child ID only if we don’t have
  // a household ID.
  if (householdId) {
    params.set('householdId', householdId)
  } else if (childId) {
    params.set('childId', childId)
  }

  return query(`post_sources`, params).then(sourcesContainer => sourcesContainer.sources)
}

const getPostWhitelist = postId => get(`posts/${postId}/whitelist`).then(whitelistContainer => whitelistContainer.users)

const queryAllPosts = params => query('posts', params)
const queryCollections = (collectionName, params) => query(`collections/${collectionName}`, params)

// This variant on queryCollections is primarily for backward compatibility with older app components.
const queryPosts = (childId, params) => {
  const finalParams = params ?? new URLSearchParams()
  finalParams.set('childId', childId)

  // Original queryPosts does return the container so that the cursor rides along.
  return queryCollections('documents', params)
}

const getProfileBlockCollection = childId => {
  const params = new URLSearchParams()
  params.set('childId', childId)

  // Backward compatibility: current calls to getProfileBlockCollection don’t do pagination.
  params.set('limit', 1000)
  return queryCollections('profile', params)
}

const patchMe = user => patch('me', user).then(userContainer => userContainer.user)

const patchProducts = (householdId, productId, payload) =>
  patch(`households/${householdId}/products/${productId}`, payload)

const postTrialMembership = householdId => post(`households/${householdId}/memberships`, {})

const postOnboardingTrialMembership = (householdId, payload) =>
  post(`onboarding/households/${householdId}/memberships`, payload)

const getOnboarding = () =>
  get('onboarding')
    .then(onboardingContainer => onboardingContainer.onboarding)
    .catch(error => {
      if (error.response?.status === HTTP_NOT_FOUND) {
        // Special case—a 404 on onboarding is a no-op: it is pretty much equivalent to “not onboarding.”
        // In this case, we return an empty object to the caller.
        return {}
      } else {
        throw error
      }
    })

const patchOnboarding = onboarding =>
  patch('onboarding', onboarding).then(onboardingContainer => onboardingContainer.onboarding)

const postSchedulingRelay = event => post('kickstarts', event)

const patchPreferences = (username, preferences) =>
  patch('userpreferences', { username, preferences }).then(userContainer => userContainer.user)

const patchEmailUnconfirmedPreferences = (username, preferences) =>
  patch('emailunconfirmed/userpreferences', { username, preferences }).then(userContainer => userContainer.user)

const getTypes = () => get('types').then(typesContainer => typesContainer.types)

const postClient = clientPayload => post('clients', clientPayload).then(clientContainer => clientContainer.client)
const patchClient = client => patch(`clients/${client.id}`, client).then(clientContainer => clientContainer.client)

const patchClientWithAuditDetails = (clientId, clientPayload) =>
  patch(`clients/${clientId}?showGoalChanges=true`, clientPayload)

const putClientTeam = client =>
  put(`clients/${client.id}/team`, client.payload).then(teamContainer => teamContainer.users)

const getContact = id => get(`contacts/${id}`).then(contactContainer => contactContainer.contact)
const postContact = params => post('contacts', params).then(container => container.contact)
const patchContact = contact => patch(`contacts/${contact.id}`, contact).then(container => container.contact)
const queryContacts = params => query('contacts', params)
const deleteContact = contact => del(`contacts/${contact.id}`)

const getContactRoles = category => {
  const params = new URLSearchParams()
  params.set('category', category)

  return query('contact-roles', params).then(container => container.roles)
}

const postPost = postPayload => post(`posts`, postPayload).then(postContainer => postContainer.post)
const patchPost = post => patch(`posts/${post.id}`, post).then(postContainer => postContainer.post)
const deletePost = post => del(`posts/${post.id}`)

const postToWhitelist = (postId, userId) => post(`posts/${postId}/whitelist`, { user_id: userId })
const deleteFromWhitelist = (postId, userId) => del(`posts/${postId}/whitelist/${userId}`)

const getAsset = (assetId, variant) => get(`assets/${assetId}/${variant}`)
const getResourceAsset = assetId => get(`resourceassets?ids=${assetId}`)

const headDirectAsset = url => directHead(url)

const uploadAsset = (url, formData) =>
  fetch(url, {
    method: METHOD_POST,
    body: formData
  }).then(noContentCheck, emitNativeError)

const postToPresignedUrl = ({ url, fields }, file) => {
  const uploadForm = new FormData()
  uploadForm.append('key', fields.key)
  Object.keys(fields)
    .filter(key => key !== 'key')
    .forEach(key => uploadForm.append(key, fields[key]))

  uploadForm.append('Content-Type', file.type)
  uploadForm.append('file', file)
  return uploadAsset(url, uploadForm)
}

const downloadAsset = (assetId, variant) => {
  const params = new URLSearchParams()
  params.set('disposition', 'attachment')
  return query(`assets/${assetId}/${variant}`, params)
}

const postAsset = ({ householdId, childId, file }) =>
  post('assets', {
    householdId,
    childId,
    mimeType: file.type,
    fileName: file.name
  }).then(assetContainer => postToPresignedUrl(assetContainer.url, file).then(() => assetContainer.id))

const postResourceAsset = (file, options) =>
  post(`resourceassets`, {
    ...options,
    mimeType: file.type
  }).then(assetContainer => postToPresignedUrl(assetContainer.url, file).then(() => assetContainer.id))

const deleteAsset = assetId => del(`assets/${assetId}`)

const queryTemplates = params => query('infoblocktemplates', params)

const getTemplate = templateId =>
  get(`infoblocktemplates/${templateId}`).then(templateContainer => templateContainer.template)

const postTemplate = templatePayload =>
  post('infoblocktemplates', templatePayload).then(templateContainer => templateContainer.template)

const patchTemplate = template =>
  patch(`infoblocktemplates/${template.id}`, template).then(templateContainer => templateContainer.template)

const deleteTemplate = templateId => del(`infoblocktemplates/${templateId}`)

const queryUsers = params => query('users', params)
const queryInternalUsers = params => query('internalusers', params)
const getUser = userId => get(`users/${userId}`).then(userContainer => model.user(userContainer.user))

const getNavigator = navigatorId => get(`navigators/${navigatorId}`)

const queryChatCandidates = params => query('chat/candidates', params)
const getChatMembers = channelId => get(`chat/channels/${channelId}/members`)
const patchChatMembers = (channelId, payload) => patch(`chat/channels/${channelId}/members`, payload)

const getGoalTeamMembers = goalId => get(`goals/${goalId}/team`)
const patchGoalTeamMembers = (goalId, payload) => patch(`goals/${goalId}/team`, payload)

const postInvitation = (clientId, name, nickname, familyName, email, type = 'TEAM') =>
  post('invitations', { client: clientId, name, nickname, familyName, email, type })

const postInvitationByNavigator = (userId, clientId, name, nickname, familyName, email, type = 'TEAM') =>
  post(`household/${userId}/invitations`, { client: clientId, name, nickname, familyName, email, type })

const getInvitations = clientId =>
  get(`clients/${clientId}/invitations`).then(invitationsContainer => invitationsContainer.invitations)

const postIndirectSignup = indirectSignupPayload => post('indirectsignups', indirectSignupPayload)
const postSignup = signupPayload => post('signups', signupPayload)
const getSignUpScreenDetails = params => query('onboarding/flows/target', params)
const postTargetOnboardingFlow = payload => postSearch('onboarding/flows/target', payload)
const postIepReviewAnalysis = payload => postAccepted('signups/iep-review/analyses', payload)

const getResource = id => get(`resources/${id}`).then(container => container.resource)
const getResources = ids => get(`resources?ids=${encodeURIComponent(ids)}`).then(container => container.resources)
const deleteResource = (id, replacementId) =>
  del(`resources/${id}${replacementId ? `?replacementId=${replacementId}` : ''}`)
const patchResource = resource => patch(`resources/${resource.id}`, resource).then(container => container.resource)
const postResource = resource => post('resources', resource).then(container => container.resource)
const postResourceSearch = params => postSearch('resourcesearch', params)
const getResourceFilters = () => get('resourcefilters').then(filters => filters.filters)

const queryTopics = params => query('resourcetopics', params)

const getFamilyObjectives = (householdId, params) => {
  const paramsToUse = params || new URLSearchParams()
  paramsToUse.set('household', householdId)
  return query('familyobjectives', params)
}

const familyObjectiveExtractor = familyObjectiveContainer => familyObjectiveContainer.familyObjective

const getFamilyObjective = familyObjectiveId =>
  get(`familyobjectives/${familyObjectiveId}`).then(familyObjectiveExtractor)

const postFamilyObjective = familyObjective => post('familyobjectives', familyObjective).then(familyObjectiveExtractor)

const patchFamilyObjective = familyObjective =>
  patch(`familyobjectives/${familyObjective.id}`, familyObjective).then(familyObjectiveExtractor)

const deleteFamilyObjective = familyObjectiveId => del(`familyobjectives/${familyObjectiveId}`)

const queryEvents = params => query('events', params)
const getEvent = id => get(`events/${id}`).then(container => container.event)
const postEvent = event => post('events', event).then(container => container.event)
const patchEvent = event => patch(`events/${event.id}`, event).then(container => container.event)
const deleteEvent = eventId => del(`events/${eventId}`)

const queryGoals = params => query('goals', params)
const getGoal = id => get(`goals/${id}`).then(container => container.goal)
const postGoal = goal => post('goals', goal).then(container => container.goal)

const patchGoal = goal => patch(`goals/${goal.id}`, goal).then(container => container.goal)
const patchGoalStep = (goalId, stepId, value) => patch(`goals/${goalId}/steps/${stepId}`, value)
const deleteGoal = goalId => del(`goals/${goalId}`)

const putGoalStatus = (goalId, status, attributes) =>
  put(`goals/${goalId}/status?force=true`, { status, additionalParams: attributes })

const getRoadmaps = () => get('roadmaps').then(container => container.roadmaps)

const postRoadmapGoals = ({ householdId, roadmapId, childId }) =>
  post(`households/${householdId}/roadmaps`, { roadmapId, childId }).then(container => container.insertedGoals)

const postRecommendedRoadmapGoals = clientId => post(`clients/${clientId}/recommended-goals`)

const queryGuides = params => query('guides', params)
const postGuide = guide => post('guides', guide).then(container => container.guide)
const patchGuide = guide => patch(`guides/${guide.id}`, guide).then(container => container.guide)
const patchStep = step => patch(`steps/${step.id}`, step).then(container => container.step)

// Note: Atypical signature for `getGuide`. To support guide variants, an optional `householdId` argument
//       determines whether variant substitution should take place. Household is used because it is the
//       household that drives variant selection.
const getGuide = (id, householdId) =>
  (() => {
    if (householdId) {
      const params = new URLSearchParams()
      params.set('householdId', householdId)
      return query(`guides/${id}`, params)
    } else {
      return get(`guides/${id}`)
    }
  })().then(container => container.guide)

const deleteGuide = guideId => del(`guides/${guideId}`).then(container => container.guide)

const postGuideVariant = (guide, variantSourceId) =>
  post(`guides/${variantSourceId}/variants`, guide).then(container => container.variant)

// Of note: these endpoint containers return both the variant and the parent variant count,
// so unlike other calls we return the entire response and don’t extract just the variant.
const patchGuideVariant = guideVariant => patch(`guidevariants/${guideVariant.id}`, guideVariant)
const deleteGuideVariant = guideVariantId => del(`guidevariants/${guideVariantId}`)

// The get guide variant container returns both as well, but the function “merges” them so that
// guide parent properties that the variant doesn’t have appear to be part of the variant,
// _except_ for the `variants` property which should exclusively be in the parent.
const getGuideVariant = guideVariantId =>
  get(`guidevariants/${guideVariantId}`).then(container => {
    const { guide, variant } = container
    const { variants, ...guideParent } = guide
    return { ...guideParent, ...variant }
  })

const queryVariants = (variantSourceId, params) => query(`guides/${variantSourceId}/variants`, params)

const queryHouseholdChecklist = (id, params) => query(`households/${id}/checklist`, params)
const queryGoalChecklist = (id, params) => query(`goals/${id}/checklist`, params)
const patchHouseholdChecklist = (id, payload) => patch(`households/${id}/checklist`, payload)

const checklistDelete = item => {
  const { id, householdId } = item
  return patchHouseholdChecklist(householdId, { delete: { id } })
}

const checklistAddFromSource = ({ householdId, goalId, guideId, stepId, key }) =>
  patchHouseholdChecklist(householdId, { addFromSource: { goalId, guideId, stepId, key } })

const checklistAddListFromSource = ({ householdId, goalId, items }) =>
  patchHouseholdChecklist(householdId, { addListFromSource: { goalId, items } })

const checklistUpdate = ({ householdId, id, changes }) =>
  patchHouseholdChecklist(householdId, { update: { id, changes } })

const checklistAdd = ({ householdId, item, before }) => patchHouseholdChecklist(householdId, { add: { item, before } })
const checklistCheck = ({ householdId, id, checked }) => checklistUpdate({ householdId, id, changes: { checked } })
const checklistMove = ({ householdId, id, before }) => patchHouseholdChecklist(householdId, { move: { id, before } })
const checklistPriority = ({ householdId, id, priority }) => checklistUpdate({ householdId, id, changes: { priority } })

const postNewSignupRequest = signupKey => postSearch(`signups/${signupKey}/refresh`)

const queryTasks = params => query(`tasks`, params)
const getTask = id => get(`tasks/${id}`).then(container => container.task)
const patchTask = task => patch(`tasks/${task.id}`, task).then(container => container.task)
const postTask = task => post('tasks', task).then(container => container.task)
const deleteTask = taskId => del(`tasks/${taskId}`)

const queryNavigatedHouseholdDetails = params =>
  query('me/navigated_household_details', params).then(container => container.navigated_household_details)
const queryGoalHouseholdDetails = params =>
  query('me/goal_household_details', params).then(container => container.goal_household_details)
const querySharedHouseholdDetails = params =>
  query('me/shared_household_details', params).then(container => container.shared_household_details)

const queryMetrics = (path, params) => query(`metrics/${path}`, params)

const queryEngagements = (path, params) =>
  queryMetrics(`engagement/${path}`, params).then(container => container.engagements)

const queryAtRiskHouseholdDetails = params => queryEngagements('atRisk', params)
const queryLowAppUsageHouseholdDetails = params => queryEngagements('appUsage', params)
const queryLowMessengerEngagementHouseholdDetails = params => queryEngagements('messenger', params)
const queryLowBinderEngagementHouseholdDetails = params => queryEngagements('binder', params)
const queryLowPlanEngagementHouseholdDetails = params => queryEngagements('plan', params)
const queryRatingsChangeHouseholdDetails = params => queryEngagements('ratingsChange', params)

const getBulkHouseholdRatings = params =>
  query('households/ratings', params).then(container => container.household_ratings)

const postBulkHouseholdRatings = params => post('households/ratings', params)
const postGoalBankDetail = goalBank => post('goaltemplates', goalBank).then(container => container.goalTemplate)
const getGoalBankDetail = (id, params) => get(`goaltemplates/${id}`, params).then(container => container.goalTemplate)
const getGoalPreview = id => get(`goal-previews/${id}`).then(container => container.goalPreview)

const postSignUpQuestionData = payload => postSearch('signups/staging', payload)

const patchGoalBankDetail = (id, goalBank) =>
  patch(`goaltemplates/${id}`, goalBank).then(container => container.goalTemplate)

const patchHouseholdGoalsReorder = (householdId, params) =>
  patch(`household/${householdId}/goals`, params).then(container => container.goals)

const patchClientGoalsReorder = (clientId, params) =>
  patch(`clients/${clientId}/goals`, params).then(container => container.goals)

const queryGoalBankDetails = params =>
  postSearch('goaltemplates/search', params).then(container => container.goalTemplates)

const queryGoalBankAutocompleteDetails = params =>
  postSearch('goaltemplates/autocomplete', params).then(container => container.goalTemplates)

const queryGoalDetailsForClient = (clientId, params) =>
  query(`goaltemplates/client/${clientId}`, params).then(container => container.goalTemplates)

const getSubscriptionPlans = () => get('subscription/plans')
const getSubscription = () => get('subscription')

// The server answers this POST with 200 on `development` today, while newer server branches promise 201 for the
// created subscription; accepting either keeps the web app working on both sides of that transition. The endpoint
// itself is not an upsert—the server refuses a second subscription—only the success statuses vary.
const createSubscription = payload => postUpsert('subscription', payload)

const previewSubscription = payload => postSearch('subscription/preview', payload)
const updateSubscription = payload => postSearch('subscription/update', payload)
const cancelSubscription = today => postSearch('subscription/cancel', { immediateRefund: today ?? false })
const undoCancelSubscription = () => postSearch('subscription/reactivate', {})
const postCancellationSurvey = payload => postAccepted('subscription/cancellation-survey', payload)

const paymentSessionDetails = sessionId => get(`payment/confirm/${sessionId}`)
const paymentInitiate = payload => postSearch('payment/initiate', payload).then(container => container.targetUrl)

const processSetupIntent = () => postSearch(`stripe/customer/setup-intent`)
const createCustomerSession = () => postSearch('stripe/customer/session')
const patchCustomerDefaultPaymentMethod = payload => patch('stripe/customer/payment-methods', payload)

const cancelAutoSubscription = () => postSearch('households/auto-subscription')

const postEmailConfirmation = key => postSearch(`confirmations/onboarding/${key}`)
const postEmailConfirmationResendRequest = email => postSearch('resend-otp', email)
const postVerifyOTP = otp => postSearch('verify-otp', otp)

const getReminders = () => get('reminders')

const queryPublicResources = payload => postSearch('resources/search/public', payload)

const postMessagesBookmark = messageId => post(`chat/messages/${messageId}/bookmarkers`)
const deleteMessagesBookmark = messageId => del(`chat/messages/${messageId}/bookmarkers`)
const getChatChannels = () => get(`me/chat-channels`)
const patchReceivedChatMessageAttribute = (messageId, payload) => patch(`chat/messages/${messageId}`, payload)

const getAndyQuestionsAvailable = () => postSearch('andy/questions/check_availability')

const queryTags = ({ context }) => {
  const params = new URLSearchParams()

  if (context) {
    params.set('context', context)
  }

  return query('tags', params).then(container => container.tags)
}

const getDiagnoses = () => queryTags({ context: 'diagnoses' })
const getDocumentTags = () => queryTags({ context: 'documents' })

const queryTypeTags = ({ q, under, topOnly } = {}) => {
  const params = new URLSearchParams()

  if (q) {
    params.set('q', q)
  }

  if (under) {
    params.set('under', under)
  }

  if (topOnly) {
    params.set('topOnly', topOnly)
  }

  return query('doctypes', params).then(container => container.doctypes)
}

const getTypeTagCategories = () => queryTypeTags({ under: 'root' })
const getTypeTags = () => queryTypeTags()

const getHealthPlans = () => get('health-plans').then(container => container.healthPlans)
const getRegionalCenters = () => get('regionalcenters').then(container => container.regionalCenters)
const getChildGoals = childId => get(`clients/${childId}/roadmap`).then(container => container)
const postChildGoal = (childId, params) => post(`clients/${childId}/roadmap`, params)

const performAuditRequest = (endpoint, childId, options = {}) => {
  const { payload, verbose, all } = options

  const params = new URLSearchParams()
  params.set('childId', childId)

  if (verbose) {
    params.set('verbose', true)
  }

  if (all) {
    params.set('all', all)
  }

  return payload ? postSearch(`${endpoint}?${params}`, payload) : query(endpoint, params)
}

const performVerboseAuditRequest = (endpoint, childId, options = {}) =>
  performAuditRequest(endpoint, childId, { ...options, verbose: true })

const postAuditStart = (auditId, childId, additionalParams = {}) =>
  performAuditRequest(`audits/${auditId}/start`, childId, { payload: additionalParams })

const getAuditUndo = (auditId, childId) => performAuditRequest(`audits/${auditId}/undo`, childId)
const getAuditRestart = (auditId, childId) => performAuditRequest(`audits/${auditId}/undo`, childId, { all: true })
const getAvailableAudits = childId => get(`clients/${childId}/audits/available`).then(container => container.audits)

const getAllAudits = childId => {
  const params = new URLSearchParams()
  params.set('childId', childId)

  return query('audits', params).then(container => container.audits)
}

const postVerboseAuditStart = (auditId, childId, additionalParams = {}) =>
  performVerboseAuditRequest(`audits/${auditId}/start`, childId, { payload: additionalParams })

const getVerboseAuditUndo = (auditId, childId) => performVerboseAuditRequest(`audits/${auditId}/undo`, childId)

const getVerboseAuditRestart = (auditId, childId) =>
  performVerboseAuditRequest(`audits/${auditId}/undo`, childId, { all: true })

const getSuggestedGoals = (childId, auditId) => {
  const params = new URLSearchParams()
  if (auditId) {
    params.set('auditId', auditId)
  }

  return query(`clients/${childId}/goals/suggestions`, params)
}

const patchSuggestedGoals = (childId, selections) => patch(`clients/${childId}/goals/suggestions`, selections)

const postAuditAnswer = (auditId, childId, answers) =>
  performAuditRequest(`audits/${auditId}/answer`, childId, { payload: answers })

const postVerboseAuditAnswer = (auditId, childId, answers) =>
  performVerboseAuditRequest(`audits/${auditId}/answer`, childId, { payload: answers })

const postVerboseAuditSkipAnswer = (auditId, childId, answers) =>
  performVerboseAuditRequest(`audits/${auditId}/skip`, childId, { payload: answers })

const postAuditClear = (auditId, childId) => post(`audits/${auditId}/clear?childId=${childId}`)

const getReceivedService = id => get(`received-services/${id}`).then(container => container.service)
const postReceivedService = serviceInstance => post('received-services', serviceInstance)
const patchReceivedService = serviceInstance => patch(`received-services/${serviceInstance.id}`, serviceInstance)
const deleteReceivedService = serviceInstanceId => del(`received-services/${serviceInstanceId}`)

const queryRoadmapTemplates = params => query('roadmaptemplates', params)

const queryRoadmapGoalTemplates = (roadmapId, params) =>
  query(`roadmaptemplates/${roadmapId}/goaltemplates`, params).then(container => container.roadmap)

const postRoadmapGoalTemplates = (clientId, templates) =>
  post(`clients/${clientId}/goalTemplates`, templates).then(container => container.added)

const getHouseholdBookmarks = householdId =>
  get(`household/${householdId}/bookmarks`).then(container => container.resourceBookmarkIds)

const removeHouseholdBookmarkPreference = async (householdId, payload) =>
  patch(`household/${householdId}/bookmarks/remove`, payload)

const getSchools = childId => get(`clients/${childId}/schools`).then(container => container.schools)
const patchSchool = (postId, payload) => patch(`clients/schools/${postId}`, payload).then(container => container.school)
const postSchool = (clientId, payload) =>
  post(`clients/${clientId}/schools`, payload).then(container => container.school)

const getOnboardingModule = householdId => get(`onboarding?householdId=${householdId}`)

const postOnboardingModule = (options = {}, payload) => {
  const { householdId, skip } = options

  const params = new URLSearchParams()

  if (householdId) {
    params.set('householdId', householdId)
  }

  if (skip) {
    params.set('skip', 'True')
  }

  return post(`onboarding?${params}`, payload)
}

const applyCouponCode = async (householdId, code, payload) =>
  patch(`households/${householdId}/coupons/${code}`, payload)

const patchUserPreferencesDueDates = (dueDateType, childId) => {
  const params = new URLSearchParams()
  if (childId) {
    params.set('childId', childId)
  }

  return patch(`users/preferences/due-dates/${dueDateType}?${params}`)
}

const deleteCouponCode = (householdId, code) => del(`households/${householdId}/coupons/${code}`)

const getClientCaInHomeSupportiveServices = clientId =>
  get(`clients/${clientId}/public-benefits/caInHomeSupportiveServices`).then(
    container => container.caInHomeSupportiveServices
  )
const postClientCaInHomeSupportiveServices = (clientId, payload) =>
  post(`clients/${clientId}/public-benefits/caInHomeSupportiveServices`, payload).then(
    container => container.caInHomeSupportiveServices
  )
const patchClientCaInHomeSupportiveServices = (centerId, payload) =>
  patch(`clients/public-benefits/caInHomeSupportiveServices/${centerId}`, payload).then(
    container => container.caInHomeSupportiveServices
  )
const deleteClientCaInHomeSupportiveServices = centerId =>
  del(`clients/public-benefits/caInHomeSupportiveServices/${centerId}`)

const getClientCaRegionalCenter = clientId =>
  get(`clients/${clientId}/public-benefits/caRegionalCenter`).then(container => container.caRegionalCenter)
const postClientCaRegionalCenter = (clientId, payload) =>
  post(`clients/${clientId}/public-benefits/caRegionalCenter`, payload).then(container => container.caRegionalCenter)
const patchClientCaRegionalCenter = (centerId, payload) =>
  patch(`clients/public-benefits/caRegionalCenter/${centerId}`, payload).then(container => container.caRegionalCenter)
const deleteClientCaRegionalCenter = centerId => del(`clients/public-benefits/caRegionalCenter/${centerId}`)

const getClientMediCalInsurances = clientId =>
  get(`clients/${clientId}/insurance/mediCal`).then(container => container.mediCal)
const getClientPrivateInsurances = clientId =>
  get(`clients/${clientId}/insurance/privateInsurance`).then(container => container.privateInsurance)
const postClientInsurance = (clientId, payload) => post(`clients/${clientId}/insurance`, payload)
const patchClientInsurance = (insuranceId, payload) => patch(`clients/insurance/${insuranceId}`, payload)
const deleteClientInsurance = insuranceId => del(`clients/insurance/${insuranceId}`)

const getCaliforniaChildrensServices = clientId =>
  get(`clients/${clientId}/public-benefits/californiaChildrensServices`).then(
    container => container.californiaChildrensServices
  )

const getProviders = params => query(`providers`, params)

const getProviderById = providerId => get(`providers/${providerId}`)

const getCareItems = () => get('care-items')

const getLanguages = () => get('languages')

const getOrganizationTypes = params => get('organizations/types', params)

const getProviderDiagnoses = () => get('diagnoses')

const searchCareOrganizations = params => query(`care-organizations`, params)

const patchCareOrganization = (organizationId, payload) => patch(`organizations/${organizationId}`, payload)

const patchCareLocation = (locationId, payload) => patch(`locations/${locationId}`, payload)

const searchCareLocations = params => query(`care-locations`, params)

const postNewProvider = provider => post('providers', provider)

const patchProvider = (providerId, payload) => patch(`providers/${providerId}`, payload)

const deleteProvider = affiliationId => del(`providers/${affiliationId}`)

const postNewAffiliation = affiliation => post('affiliations', affiliation)

const patchAffiliation = (affiliationId, payload) => patch(`affiliations/${affiliationId}`, payload)

const deleteAffiliation = affiliationId => del(`affiliations/${affiliationId}`)

const getOrganizations = params => query('organizations', params)

const getOrganizationById = organizationId => get(`organizations/${organizationId}`)

const getAffiliatedCareItems = params => query(`affiliated-care-items`, params)

const searchProviders = params => query(`care-providers`, params)

const postGeneralizedList = payload => post('generalized-lists', payload)

const getGeneralizedLists = params => query('generalized-lists', params)

const getGeneralizedListById = listId => get(`generalized-lists/${listId}`)

const patchGeneralizedList = (listId, payload) => patch(`generalized-lists/${listId}`, payload)

const deleteGeneralizedList = listId => del(`generalized-lists/${listId}`)

const getAffiliationGeneralizedLists = affiliationId => get(`affiliations/${affiliationId}/generalized-lists`)

const putAffiliationGeneralizedLists = (affiliationId, listIds) =>
  put(`affiliations/${affiliationId}/generalized-lists`, { lists: listIds })

const patchGeneralizedListAffiliations = (listId, affiliationIds) =>
  patch(`generalized-lists/${listId}/add-affiliations`, { affiliations: affiliationIds })

const postGeneralizedListCategory = (listId, payload) => post(`generalized-lists/${listId}/categories`, payload)

const patchGeneralizedListCategory = (listId, categoryId, payload) =>
  patch(`generalized-lists/${listId}/categories/${categoryId}`, payload)

const deleteGeneralizedListCategory = (listId, categoryId) =>
  del(`generalized-lists/${listId}/categories/${categoryId}`)

const putGeneralizedListItemCategories = (listId, categoriesByItem) =>
  put(`generalized-lists/${listId}/item-categories`, { items: categoriesByItem })

const printGeneralizedList = listId => get(`generalized-lists/${listId}/print`)

const getAffiliationAdditionalFields = affiliationId => get(`affiliations/${affiliationId}/additional-fields`)

const patchAffiliationAdditionalFields = (affiliationId, valuesByCareItem) =>
  patch(`affiliations/${affiliationId}/additional-fields`, { additionalFields: valuesByCareItem })

const getCareItemFieldDefinitions = careItemId => get(`care-items/${careItemId}/field-definitions`)

const putCareItemFieldDefinitions = (careItemId, fieldDefinitions) =>
  put(`care-items/${careItemId}/field-definitions`, { fieldDefinitions })

const getTheraphies = () => get('therapies')

const getClientTherapies = clientId => get(`clients/${clientId}/therapies`)
const postClientTherapy = (clientId, payload) => post(`clients/${clientId}/therapies`, payload)
const patchClientTherapy = (therapyId, payload) => patch(`therapies/${therapyId}`, payload)
const deleteClientTherapy = therapyId => del(`therapies/${therapyId}`)

const getClientMedicalNeeds = clientId => get(`clients/${clientId}/medical-needs`)
const postClientMedicalNeed = (clientId, payload) => post(`clients/${clientId}/medical-needs`, payload)
const patchClientMedicalNeed = (needId, payload) => patch(`medical-needs/${needId}`, payload)
const deleteClientMedicalNeed = needId => del(`medical-needs/${needId}`)

const getSupportMyChildWorks = clientId => get(`clients/${clientId}/descriptors/works`)
const getSupportMyChildDoesntWork = clientId => get(`clients/${clientId}/descriptors/doesntWork`)
const getClientStrengthsAndLoves = clientId => get(`clients/${clientId}/descriptors/strength`)

const postClientDescriptor = (clientId, payload) => post(`clients/${clientId}/descriptors`, payload)
const patchClientDescriptor = (descriptorId, payload) => patch(`clients/descriptors/${descriptorId}`, payload)
const deleteClientDescriptor = descriptorId => del(`clients/descriptors/${descriptorId}`)

const getClientDiagnosis = clientId => get(`clients/${clientId}/diagnosis`)
const getClientDiagnosisSymptoms = clientId => get(`clients/${clientId}/symptoms`)

const postClientDiagnosisSymptoms = (clientId, payload) => post(`clients/${clientId}/symptoms`, payload)
const patchClientDiagnosisSymptoms = (symptomId, payload) => patch(`symptoms/${symptomId}`, payload)
const deleteClientDiagnosisSymptoms = symptomId => del(`symptoms/${symptomId}`)
const postConfirmEmailUploads = payload => post('binder/email-uploads/confirm', payload)
const getAlerts = () => get('alerts').then(({ alert }) => alert)

export {
  getFeed,
  getReviewPrompt,
  postReviewPromptEvent,
  getHousehold,
  patchHousehold,
  patchHouseholdAndyPermission,
  getBulkHouseholdRatings,
  postBulkHouseholdRatings,
  getGoalHouseholds,
  getNavigatedHouseholds,
  getClients,
  getClient,
  getClientTeam,
  putClientTeam,
  getContact,
  postContact,
  patchContact,
  queryContacts,
  deleteContact,
  getInvitations,
  postDocumentExtraction,
  getIep,
  getIpp,
  generatePlanReportForPrint,
  previewPlanReport,
  savePlanReportToBinder,
  getPostById,
  getEligibleDocuments,
  getProfileDocuments,
  postProfileDocuments,
  postCreateAnotherProfileDocument,
  generateProfileDocuments,
  getDocumentGenerationStatus,
  patchProfileSummaryOverride,
  patchRecordVisibility,
  patchWizardBlocks,
  patchWizardSchoolBlock,
  postWizardCheckpoint,
  getHouseholdRatings,
  postHouseholdRating,
  downloadPostArchive,
  downloadPostDocument,
  getPostDocument,
  getPostGoals,
  putPostGoals,
  getPostSources,
  getPostWhitelist,
  getProfileBlockCollection,
  patchMe,
  patchProducts,
  postTrialMembership,
  getOnboarding,
  patchOnboarding,
  postSchedulingRelay,
  patchPreferences,
  getTypes,
  postClient,
  patchClient,
  patchClientWithAuditDetails,
  patchIep,
  getSchoolPlanDocumentExtractionData,
  patchIepExtractionData,
  getIppExtractionData,
  patchIppExtractionData,
  patchIepVisibility,
  postIepConcern,
  patchIepConcern,
  patchIepRecommendationStatus,
  patchIepGoalReviewStatus,
  patchIepGoalReviewStatuses,
  patchIepServiceReviewStatus,
  patchIepServiceReviewStatuses,
  patchIepAccommodationReviewStatus,
  patchIepAccommodationReviewStatuses,
  patchIepPlopReviewStatus,
  postIppConcern,
  patchIppConcern,
  patchIppRecommendationStatus,
  patchIppPriorityReviewStatus,
  patchIppPriorityReviewStatuses,
  patchIppOutcomeReviewStatus,
  patchIppOutcomeReviewStatuses,
  patchIppGoalReviewStatus,
  patchIppGoalReviewStatuses,
  patchIppServiceReviewStatus,
  patchIppServiceReviewStatuses,
  patchIppSupportReviewStatus,
  patchIppSupportReviewStatuses,
  postPost,
  patchPost,
  deletePost,
  postToWhitelist,
  deleteFromWhitelist,
  queryCollections,
  queryPosts,
  queryAllPosts,
  headDirectAsset,
  getAsset,
  getResourceAsset,
  downloadAsset,
  postToPresignedUrl,
  postAsset,
  postResourceAsset,
  deleteAsset,
  queryTemplates,
  getTemplate,
  postTemplate,
  patchTemplate,
  deleteTemplate,
  queryUsers,
  queryInternalUsers,
  getUser,
  getNavigator,
  queryChatCandidates,
  getChatMembers,
  patchChatMembers,
  getGoalTeamMembers,
  patchGoalTeamMembers,
  postSignup,
  getSignUpScreenDetails,
  postTargetOnboardingFlow,
  postIepReviewAnalysis,
  postIndirectSignup,
  postInvitation,
  postInvitationByNavigator,
  getResource,
  getResources,
  deleteResource,
  postResource,
  postResourceSearch,
  patchResource,
  getResourceFilters,
  queryTopics,
  getFamilyObjectives,
  getFamilyObjective,
  postFamilyObjective,
  patchFamilyObjective,
  deleteFamilyObjective,
  queryEvents,
  getEvent,
  postEvent,
  patchEvent,
  deleteEvent,
  queryGoals,
  getGoal,
  postGoal,
  patchGoal,
  patchGoalStep,
  putGoalStatus,
  deleteGoal,
  getRoadmaps,
  postRoadmapGoals,
  queryHouseholdChecklist,
  queryGoalChecklist,
  queryGuides,
  postGuide,
  patchGuide,
  patchStep,
  checklistAdd,
  checklistAddFromSource,
  checklistAddListFromSource,
  checklistCheck,
  checklistDelete,
  checklistMove,
  checklistPriority,
  checklistUpdate,
  getGuide,
  deleteGuide,
  postGuideVariant,
  patchGuideVariant,
  deleteGuideVariant,
  queryVariants,
  getGuideVariant,
  postNewSignupRequest,
  queryTags,
  getDiagnoses,
  getDocumentTags,
  queryTypeTags,
  getTypeTagCategories,
  getTypeTags,
  queryTasks,
  getTask,
  patchTask,
  postTask,
  deleteTask,
  queryNavigatedHouseholdDetails,
  queryGoalHouseholdDetails,
  querySharedHouseholdDetails,
  queryMetrics,
  queryAtRiskHouseholdDetails,
  queryLowAppUsageHouseholdDetails,
  queryLowMessengerEngagementHouseholdDetails,
  queryLowBinderEngagementHouseholdDetails,
  queryLowPlanEngagementHouseholdDetails,
  queryRatingsChangeHouseholdDetails,
  queryGoalBankDetails,
  getGoalBankDetail,
  getGoalPreview,
  postGoalBankDetail,
  patchGoalBankDetail,
  patchHouseholdGoalsReorder,
  queryGoalBankAutocompleteDetails,
  queryGoalDetailsForClient,
  getSubscriptionPlans,
  getSubscription,
  createSubscription,
  previewSubscription,
  updateSubscription,
  cancelSubscription,
  undoCancelSubscription,
  postCancellationSurvey,
  paymentSessionDetails,
  paymentInitiate,
  patchCustomerDefaultPaymentMethod,
  createCustomerSession,
  processSetupIntent,
  cancelAutoSubscription,
  postConfirmEmailUploads,
  postEmailConfirmation,
  postEmailConfirmationResendRequest,
  getReminders,
  queryPublicResources,
  postMessagesBookmark,
  deleteMessagesBookmark,
  getChatChannels,
  getHealthPlans,
  getRegionalCenters,
  getChildGoals,
  postChildGoal,
  postAuditStart,
  getAuditUndo,
  getAuditRestart,
  getAvailableAudits,
  getAlerts,
  getAndyQuestionsAvailable,
  getAllAudits,
  postVerboseAuditStart,
  getVerboseAuditUndo,
  getVerboseAuditRestart,
  getSuggestedGoals,
  patchSuggestedGoals,
  postAuditAnswer,
  postVerboseAuditAnswer,
  postVerboseAuditSkipAnswer,
  postAuditClear,
  getReceivedService,
  postReceivedService,
  patchReceivedService,
  deleteReceivedService,
  queryRoadmapTemplates,
  queryRoadmapGoalTemplates,
  postRoadmapGoalTemplates,
  patchEmailUnconfirmedPreferences,
  patchClientGoalsReorder,
  postVerifyOTP,
  postRecommendedRoadmapGoals,
  getHouseholdBookmarks,
  removeHouseholdBookmarkPreference,
  getContactRoles,
  postOnboardingTrialMembership,
  getSchools,
  patchSchool,
  postSchool,
  postOnboardingModule,
  getOnboardingModule,
  applyCouponCode,
  patchUserPreferencesDueDates,
  getClientCaInHomeSupportiveServices,
  postClientCaInHomeSupportiveServices,
  patchClientCaInHomeSupportiveServices,
  deleteClientCaInHomeSupportiveServices,
  getClientCaRegionalCenter,
  postClientCaRegionalCenter,
  patchClientCaRegionalCenter,
  deleteClientCaRegionalCenter,
  postClientInsurance,
  patchClientInsurance,
  deleteClientInsurance,
  getClientMediCalInsurances,
  getClientPrivateInsurances,
  getCaliforniaChildrensServices,
  getProviders,
  getProviderById,
  getCareItems,
  getLanguages,
  getOrganizationTypes,
  getProviderDiagnoses,
  searchCareOrganizations,
  patchCareOrganization,
  patchCareLocation,
  searchCareLocations,
  postNewProvider,
  patchProvider,
  deleteProvider,
  postNewAffiliation,
  patchAffiliation,
  deleteAffiliation,
  getOrganizations,
  getOrganizationById,
  getAffiliatedCareItems,
  searchProviders,
  postGeneralizedList,
  getGeneralizedLists,
  getGeneralizedListById,
  patchGeneralizedList,
  deleteGeneralizedList,
  getAffiliationGeneralizedLists,
  putAffiliationGeneralizedLists,
  patchGeneralizedListAffiliations,
  postGeneralizedListCategory,
  patchGeneralizedListCategory,
  deleteGeneralizedListCategory,
  putGeneralizedListItemCategories,
  printGeneralizedList,
  getAffiliationAdditionalFields,
  patchAffiliationAdditionalFields,
  getCareItemFieldDefinitions,
  putCareItemFieldDefinitions,
  getTheraphies,
  getClientTherapies,
  patchClientTherapy,
  postClientTherapy,
  deleteClientTherapy,
  getClientMedicalNeeds,
  postClientMedicalNeed,
  patchClientMedicalNeed,
  deleteClientMedicalNeed,
  getSupportMyChildWorks,
  getSupportMyChildDoesntWork,
  getClientStrengthsAndLoves,
  postClientDescriptor,
  patchClientDescriptor,
  deleteClientDescriptor,
  getClientDiagnosis,
  getClientDiagnosisSymptoms,
  postClientDiagnosisSymptoms,
  patchClientDiagnosisSymptoms,
  deleteClientDiagnosisSymptoms,
  patchReceivedChatMessageAttribute,
  postHideAnnouncements,
  deleteCouponCode,
  postSignUpQuestionData
}
